import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import AnnotatePlateStep from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateStep';
import {
  removeParameter,
  toggleParameter,
} from 'samples/plate-maps/display-layer-controls/DataParameterControlUtils';
import {PickableLayersProps} from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerSidebar';
import {
  confirmDeleteSchemaField,
  toggleSchemaField,
} from 'samples/plate-maps/display-layer-controls/WellSchemaFieldControlUtils';
import FillPlateReagentStep from 'samples/plate-maps/fill-plate-modal/FillPlateReagentStep';
import {PlateModalMachineAction} from 'samples/plate-maps/fill-plate-modal/PlateModalMachineProvider';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  ConcentrationAttribute,
  DisplayLayerMetadata,
  DisplayLayerOptionSection,
  PlateDataParameter,
  PlateMap,
  PlateMapDisplayLayerAttribute,
  PlateMapProcedureContext,
  PlateMapWellDisplayLayer,
  PlateModalAnalyticsMetadata,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchematizedAttribute,
  SchematizedAttributeType,
  TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDisplayLayerAttributeKey,
  getPlateDataParametersOptions,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import {ValidationErrorsByLayerAndLevel} from 'samples/plate-maps/validation/ValidationTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {omit} from 'util/omitKey';
import {assertNever} from 'util/Types';

import {ConcentrationLayersProps} from '../display-layer-controls/PlateMapDisplayLayerSidebar';
import {FillPlateModalState} from '../fill-plate-modal/machine/FillPlateModalConstants';
import {PlateMapSidebar} from '../sidebar/PlateMapSidebar';

export interface DefinePlateMapStepProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  initialPlateMap: PlateMap;
  initialSelectedWellIndices: Set<number>;
  initialSelectedAttribute: PlateMapDisplayLayerAttribute;
  maxPlateViewHeight: string;
  disabledSections: Array<DisplayLayerOptionSection>;
  modalRef?: RefObject<HTMLDivElement>;
  analyticsMetadata: PlateModalAnalyticsMetadata;
  selectedPlateDataParameterIds?: Set<string>;
  plateState?: FillPlateModalState;
  sendPlateModalMachineAction?: PlateModalMachineAction;
  showFinalMeasurements: boolean;
  procedureContext: PlateMapProcedureContext;
  plateMapType: PlateMapType;
  plateDataParameters: Array<PlateDataParameter>;
  wellSchemaLayers: Array<DisplayLayerMetadata> | null;
  selectedWellSchemaFieldIds: Set<string>;
  onAddSelectedDataParameter: (versionId: string) => void;
  onRemoveSelectedDataParameter: (versionId: string) => void;
  onAddSelectedSchemaField: (schemaFieldId: string) => void;
  onRemoveSelectedSchemaField: (schemaFieldId: string) => void;
  initialShouldPreviewWellRoles: boolean;
  validationErrors: ValidationErrorsByLayerAndLevel | null;
  onDismissErrors: (errorUuids: Set<string>) => void;
  concentrationLayersProps?: ConcentrationLayersProps;
}

/**
 * Content of modal step that optionally applies well role, schema field and parameter annotations
 * to a plate and defines sources to transfer to a plate.
 */
export default function DefinePlateMapStep({
  plateMap,
  setPlateMap,
  initialPlateMap,
  initialSelectedWellIndices,
  initialSelectedAttribute,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  disabledSections,
  plateState,
  sendPlateModalMachineAction,
  showFinalMeasurements,
  selectedPlateDataParameterIds = new Set(),
  selectedWellSchemaFieldIds,
  wellSchemaLayers,
  plateMapType,
  onAddSelectedDataParameter,
  onRemoveSelectedDataParameter,
  onAddSelectedSchemaField,
  onRemoveSelectedSchemaField,
  initialShouldPreviewWellRoles,
  plateDataParameters,
  procedureContext,
  validationErrors,
  onDismissErrors,
  concentrationLayersProps,
}: DefinePlateMapStepProps): JSX.Element {
  const displayState = usePlateMapDisplayAttributeState(plateMap, initialSelectedAttribute, false);
  const plateDataParametersOptions = getPlateDataParametersOptions(plateDataParameters);
  const hiddenSections = !shouldShowTestOrderDilutionInfo(
    procedureContext.procedureStudyDesignType,
    plateMapType
  )
    ? [DisplayLayerOptionSection.TEST_METADATA]
    : [];

  useEffect(() => {
    if (plateState?.reagentGroupsById != null) {
      // Keep displayState assignment groups for the sidebar in sync with the reagent groups in plateState
      const updatedReagentGroups: {
        [key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
      } = {
        ...displayState.assignmentGroupsPerAttribute,
        [REAGENTS_ATTRIBUTE_SENTINEL]: plateState.reagentGroupsById,
      };
      // Clear dilution factor assignment groups when reagents are updated since they may have changed.
      // Will be computed when the dilution factor layer is visited.
      displayState.setAssignmentGroupsPerAttribute(
        omit(updatedReagentGroups, TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL)
      );
    }
    // Only update when the reagent groups change, including displayState in deps causes infinite rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateState?.reagentGroupsById]);

  function onSelectAttribute(attr: PlateMapDisplayLayerAttribute): void {
    let layerType: string;
    if (typeof attr === 'string') {
      layerType = attr;
    } else if (attr.type === CONCENTRATION_ATTRIBUTE_SENTINEL) {
      layerType = 'concentration';
    } else {
      // Schema field attributes
      layerType = (attr as SchematizedAttribute).schemaField.fieldType;
    }
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.SELECTED_ANNOTATION_LAYER, {
      ...analyticsMetadata,
      layerType,
    });
    displayState.setSelectedAttribute(attr);
  }

  function onToggleParameter({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }): void {
    if (sendPlateModalMachineAction == null) {
      return;
    }
    toggleParameter({
      newSelectedIds,
      oldSelectedIds: selectedPlateDataParameterIds,
      plateMap,
      initialPlateMap,
      plateDataParameters,
      onConfirmAdd: (versionId: string) => {
        onAddSelectedDataParameter(versionId);
        onSelectAttribute(toggledAttribute);
      },
      onConfirmDelete: onRemoveSelectedDataParameter,
      onLaunchConfirmDeleteModal,
    });
  }

  function onRemoveParameter(idToRemove: string): void {
    if (sendPlateModalMachineAction == null) {
      return;
    }
    removeParameter({
      idToRemove,
      plateMap,
      initialPlateMap,
      plateDataParameters,
      onConfirmDelete: onRemoveSelectedDataParameter,
    });
  }

  function onToggleSchemaField({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }): void {
    const analyticsAction =
      newSelectedIds.length > selectedWellSchemaFieldIds.size
        ? STORABLE_ANALYTICS_ACTIONS.ADDED_ANNOTATION_LAYER
        : STORABLE_ANALYTICS_ACTIONS.REMOVED_ANNOTATION_LAYER;
    analytics.trackEvent(analyticsAction, {...analyticsMetadata, layerType: 'schema field'});
    toggleSchemaField({
      newSelectedIds,
      oldSelectedIds: selectedWellSchemaFieldIds,
      plateMap,
      initialPlateMap,
      onConfirmAdd: (schemaFieldId: string) => {
        onAddSelectedSchemaField(schemaFieldId);
        onSelectAttribute(toggledAttribute);
      },
      onConfirmDelete: onRemoveSelectedSchemaField,
      onLaunchConfirmDeleteModal,
    });
  }

  function onRemoveSchemaField(schemaFieldId: string): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.REMOVED_ANNOTATION_LAYER, {
      ...analyticsMetadata,
      layerType: 'schema field',
    });
    confirmDeleteSchemaField({
      plateMap,
      initialPlateMap,
      schemaFieldId,
      onConfirmDelete: onRemoveSelectedSchemaField,
    });
  }

  function onRemoveLayer(attribute: SchematizedAttribute | ConcentrationAttribute): void {
    switch (attribute.type) {
      case CONCENTRATION_ATTRIBUTE_SENTINEL: {
        concentrationLayersProps?.onRemoveLayer(attribute.key);
        break;
      }
      case SchematizedAttributeType.DATA_PARAMETER: {
        onRemoveParameter(attribute.key);
        break;
      }
      case SchematizedAttributeType.WELL_SCHEMA_FIELD:
        onRemoveSchemaField(attribute.key);
        break;
      default:
        assertNever(attribute);
    }
  }

  const parameterLayersProps: PickableLayersProps = {
    enableAddRemoveLayers: plateDataParameters.length > 0,
    availableLayers: plateDataParametersOptions,
    selectedLayerIds: selectedPlateDataParameterIds,
    onToggleLayer: onToggleParameter,
    onRemoveLayer: onRemoveParameter,
  };

  return (
    <div
      className={css(styles.stepContents)}
      {...getTestProps({component: 'DefinePlateMapStep', element: 'root'})}
    >
      {wellSchemaLayers == null && <CenteredHorizontalThrobber />}
      {wellSchemaLayers != null && (
        <PlateMapSidebar
          plateMapType={plateMapType}
          plateMap={plateMap}
          selectedAttributeKey={getDisplayLayerAttributeKey(displayState.selectedAttribute)}
          setSelectedAttribute={onSelectAttribute}
          assignmentGroups={displayState.assignmentGroupsPerAttribute}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          groupedValidationErrors={validationErrors ?? {}}
          wellSchemaLayersProps={{
            enableAddRemoveLayers:
              !disabledSections.includes(DisplayLayerOptionSection.SCHEMA_FIELDS) &&
              wellSchemaLayers.length > 0,
            availableLayers: wellSchemaLayers,
            selectedLayerIds: selectedWellSchemaFieldIds,
            onToggleLayer: onToggleSchemaField,
            onRemoveLayer: onRemoveSchemaField,
          }}
          parameterLayersProps={parameterLayersProps}
          concentrationLayersProps={concentrationLayersProps}
        />
      )}
      <div className={css(styles.border)} />
      {plateState != null &&
      sendPlateModalMachineAction != null &&
      displayState.selectedAttribute === REAGENTS_ATTRIBUTE_SENTINEL ? (
        <FillPlateReagentStep
          {...plateState}
          sendPlateModalMachineAction={sendPlateModalMachineAction}
          initialPlateMap={plateMap}
          previewPlateMap={plateState.previewPlateMap!}
          maxPlateViewHeight={maxPlateViewHeight}
          analyticsMetadata={analyticsMetadata}
          modalRef={modalRef}
          procedureContext={procedureContext}
          initialShouldPreviewWellRoles={initialShouldPreviewWellRoles}
          plateMapType={plateMapType}
          groupedValidationErrors={validationErrors ?? {}}
          onDismissErrors={onDismissErrors}
        />
      ) : (
        <AnnotatePlateStep
          plateMap={plateMap}
          setPlateMap={setPlateMap}
          initialSelectedWellIndices={initialSelectedWellIndices}
          maxPlateViewHeight={maxPlateViewHeight}
          displayState={displayState}
          analyticsMetadata={analyticsMetadata}
          modalRef={modalRef}
          showFinalMeasurements={showFinalMeasurements}
          isViewOnly={displayState.displayLayer === PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR}
          groupedValidationErrors={validationErrors ?? {}}
          onRemoveLayer={onRemoveLayer}
          onDismissErrors={onDismissErrors}
          plateMapType={plateMapType}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  stepContents: {
    display: 'flex',
    maxHeight: 'calc(100vh - 280px)',
    overflow: 'hidden',
    margin: '-16px',
    padding: '16px',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    margin: '-16px 16px -16px 0',
  },
});
